// The system calendar's two reads (mig 20261017240000).
// Spec: docs/specs/system-calendar.md
//
// Deliberately two queries, not one:
//
//   useCalendarMonth  → calendar_day_summary, one row per (day x source).
//                       Drives the month grid and the agenda. A KF49 packing
//                       day is ~200 deliveries and ~1 400 pallets, so a month
//                       of EVENT rows is six figures; aggregated it is at most
//                       days x sources. The grid must never fetch event rows.
//
//   useCalendarDay    → calendar_day_detail, the events on ONE day. Enabled
//                       only while a day is open, so a month of browsing costs
//                       one read per day actually looked at.
//
// The supabase client here is untyped (createClient with no <Database>
// generic — src/lib/supabase.ts), so the RPC row shapes are declared locally
// and cast, exactly as the page this replaces did with packhouse_calendar_events.
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useActiveTenant } from '@/lib/useActiveTenant'
import { withReadTimeout } from '@/lib/saveTimeout'
import { BAND_RANK, type CalendarBand, type CalendarTense } from '@/lib/calendarSources'

/** One (day x source) aggregate — the month grid's unit of work. */
export interface CalendarDaySummary {
  day:          string          // YYYY-MM-DD, already a SAST calendar day
  source_type:  string
  tense:        CalendarTense
  label:        string
  event_count:  number
  metric_value: number | null
  metric_unit:  string | null
  worst_band:   CalendarBand
  sample_label: string | null   // set only when event_count === 1
  sort_order:   number
}

/** One event on one day — the drawer's unit of work. */
export interface CalendarDayEvent {
  source_type:  string
  tense:        CalendarTense
  label:        string
  source_id:    string
  title:        string
  subtitle:     string | null
  occurred_at:  string | null   // null for `due` rows: a deadline has no clock
  metric_value: number | null
  metric_unit:  string | null
  status:       string | null
  band:         CalendarBand
  link_path:    string
  actor_name:   string | null
  source_total: number          // true total, so the UI can say what it withheld
  sort_order:   number
}

/**
 * The BACKDROP of a day — holiday, weather, volume (mig 20261017250000).
 * Not events: a holiday is a property of the day, not something that happened
 * on it, so it gets no chip, no source filter and no drawer section.
 */
export interface CalendarDayContext {
  day:                 string
  holiday_name:        string | null
  is_paid_holiday:     boolean | null
  // ⚠️ Every NUMERIC below arrives from PostgREST as a STRING (H85 — documented
  // in src/lib/weather.ts for rain_mm specifically). The type says so rather
  // than lying, and DayContextLine coerces. Do not `.toFixed()` these directly.
  /** AVERAGE across the tenant's weather points — see the migration header. */
  rain_mm:             number | string | null
  /** MAX across points: the hottest it got anywhere. */
  tmax_c:              number | string | null
  tmin_c:              number | string | null
  point_count:         number
  /** Withheld (null) from callers holding neither `runs` nor `pallets`. */
  packed_kg:           number | string | null
  tipped_kg:           number | string | null
  cartons:             number | string | null
  /** The rollup is periodic — a null/old stamp means "not counted yet". */
  rollup_refreshed_at: string | null
}

const calendarKeys = {
  month: (tenantId: string | null, from: string, to: string) =>
    ['calendar-month', tenantId, from, to] as const,
  horizon: (tenantId: string | null, from: string, to: string) =>
    ['calendar-horizon', tenantId, from, to] as const,
  day: (tenantId: string | null, day: string) =>
    ['calendar-day', tenantId, day] as const,
  context: (tenantId: string | null, from: string, to: string) =>
    ['calendar-context', tenantId, from, to] as const,
}

/**
 * The aggregates for a date window. Tenant id is in the KEY but never sent —
 * the RPC reads current_tenant_id() server-side. It is in the key so switching
 * tenant cannot show the previous tenant's cached month for a frame.
 */
export function useCalendarMonth(from: string, to: string) {
  const { data: activeTenant } = useActiveTenant()
  const tenantId = activeTenant?.tenantId ?? null

  return useQuery({
    queryKey: calendarKeys.month(tenantId, from, to),
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await withReadTimeout(
        supabase.rpc('calendar_day_summary', { p_from: from, p_to: to }),
        'calendar: calendar_day_summary',
      )
      if (error) throw error
      return (data ?? []) as CalendarDaySummary[]
    },
  })
}

/**
 * The DEADLINE horizon: due + planned across today−2y … today+1y (HORIZON in
 * pageRules.ts). The page this replaced stopped 30 days back, matching
 * `packhouse_calendar_events`; that floor dropped anything more than a month
 * overdue from the "Overdue" tile while it was still overdue, so this reads
 * further back. Only these two tenses are read, so widening the floor touches
 * deadline tables, never pallets.
 *
 * This exists because the grid is month-scoped and deadlines are not. Reading
 * only the anchored month would mean a certificate that expired last month
 * vanishes from the agenda AND from the "Overdue" tile the moment the calendar
 * rolls over — the page's single most important job, silently regressed. The
 * `done` plane stays month-scoped: "what happened" is a question about a month,
 * "what am I about to miss" is not.
 */
export function useCalendarHorizon(from: string, to: string, enabled = true) {
  const { data: activeTenant } = useActiveTenant()
  const tenantId = activeTenant?.tenantId ?? null

  return useQuery({
    queryKey: calendarKeys.horizon(tenantId, from, to),
    enabled: enabled && !!tenantId,
    queryFn: async () => {
      const { data, error } = await withReadTimeout(
        supabase.rpc('calendar_day_summary', {
          p_from: from, p_to: to, p_tenses: ['due', 'planned'],
        }),
        'calendar: calendar_day_summary (horizon)',
      )
      if (error) throw error
      return (data ?? []) as CalendarDaySummary[]
    },
  })
}

/**
 * The events on one day. `day` null = no day open, so the query stays idle
 * rather than firing a read for a drawer nobody has opened.
 */
// Guard states, not logic: the tenant gate, the day gate, and the error throw.
// fallow-ignore-next-line complexity
export function useCalendarDay(day: string | null) {
  const { data: activeTenant } = useActiveTenant()
  const tenantId = activeTenant?.tenantId ?? null

  return useQuery({
    queryKey: calendarKeys.day(tenantId, day ?? ''),
    enabled: !!tenantId && !!day,
    queryFn: async () => {
      const { data, error } = await withReadTimeout(
        supabase.rpc('calendar_day_detail', { p_day: day }),
        'calendar: calendar_day_detail',
      )
      if (error) throw error
      return (data ?? []) as CalendarDayEvent[]
    },
  })
}

/**
 * The day backdrop for a window. Separate from the event reads on purpose: it
 * changes on a different clock (a holiday calendar is set once a year, weather
 * lands nightly), so tying it to the event query would refetch three tables
 * every time a chip is tapped.
 *
 * A failure here must never blank the calendar — the events are the page and
 * the backdrop is decoration, so callers read `data ?? []` and render without
 * it rather than surfacing an error.
 */
export function useCalendarContext(from: string | null, to: string | null) {
  const { data: activeTenant } = useActiveTenant()
  const tenantId = activeTenant?.tenantId ?? null

  return useQuery({
    // null window = the caller already has this day's context (the drawer,
    // handed it by the page). Idle rather than a redundant round-trip.
    queryKey: calendarKeys.context(tenantId, from ?? '', to ?? ''),
    enabled: !!tenantId && !!from && !!to,
    queryFn: async () => {
      const { data, error } = await withReadTimeout(
        supabase.rpc('calendar_day_context', { p_from: from!, p_to: to! }),
        'calendar: calendar_day_context',
      )
      if (error) throw error
      return (data ?? []) as CalendarDayContext[]
    },
  })
}

/** Context rows keyed by day, for O(1) lookup from a cell renderer. */
export function contextByDay(rows: CalendarDayContext[]): Map<string, CalendarDayContext> {
  return new Map(rows.map(r => [r.day, r]))
}

// ---------------------------------------------------------------------------
// Shaping
// ---------------------------------------------------------------------------

/**
 * Union the month rows with the deadline horizon for the agenda, de-duplicated
 * on (day, source_type) — the two windows overlap wherever a deadline falls
 * inside the anchored month, and the same group must not be listed twice.
 */
export function mergeSummaries(
  monthRows: CalendarDaySummary[], horizonRows: CalendarDaySummary[],
): CalendarDaySummary[] {
  const seen = new Set(monthRows.map(r => `${r.day}:${r.source_type}`))
  return [...monthRows, ...horizonRows.filter(r => !seen.has(`${r.day}:${r.source_type}`))]
}

/**
 * Within-day chip order, shared by the month grid and the week columns:
 * deadlines first, then planned, then what happened — and within a tense, most
 * urgent first, then the registry's order. Defined ONCE because the two views
 * disagreeing about which chip leads a day is exactly the kind of drift nobody
 * reports and everybody half-notices.
 */
export function compareSummaryChips(a: CalendarDaySummary, b: CalendarDaySummary): number {
  const rank = (r: CalendarDaySummary) => (r.tense === 'due' ? 0 : r.tense === 'planned' ? 1 : 2)
  return (rank(a) - rank(b))
    || (BAND_RANK[a.worst_band] - BAND_RANK[b.worst_band])
    || (a.sort_order - b.sort_order)
}

/**
 * Day totals for the density bar: the biggest single-source event count on a
 * day, relative to the month's busiest. Deliberately a COUNT, not a metric —
 * sources measure different things (bins, cartons, kg, fails) and summing
 * across them would be adding tonnes to QC failures.
 */
// A two-pass accumulate-then-normalise. The branches are the `done`-only
// filter and the empty-input floor; splitting them would need the intermediate
// map to cross a function boundary for no gain.
// fallow-ignore-next-line complexity
export function dayWeights(rows: CalendarDaySummary[]): Map<string, number> {
  const totals = new Map<string, number>()
  for (const r of rows) {
    if (r.tense !== 'done') continue        // deadlines are not "how busy was it"
    totals.set(r.day, (totals.get(r.day) ?? 0) + r.event_count)
  }
  const max = Math.max(1, ...totals.values())
  const out = new Map<string, number>()
  for (const [day, n] of totals) out.set(day, n / max)
  return out
}
